'use client';

import { useState, useEffect, useContext, useRef, useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMediaQuery } from '@custom-react-hooks/use-media-query';

import Highcharts from 'highcharts/esm/highcharts.js';
import HighchartsReact from 'highcharts-react-official';

import 'highcharts/esm/highcharts-more.js';
import 'highcharts/esm/modules/exporting.js';
import 'highcharts/esm/modules/annotations.js';
import 'highcharts/esm/modules/offline-exporting.js';
import 'highcharts/esm/modules/export-data.js';
import 'highcharts/esm/modules/heatmap.js';
import 'highcharts/esm/modules/treemap.js';
import 'highcharts/esm/modules/timeline.js';

import * as XLSX from 'xlsx';

import OverviewSelect from '../OverviewSelect';
import YearPicker from '../YearPicker';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import DataErrorItem from '../DataErrorItem/DataErrorItem';

import { CountryContext } from '../../utils/country-provider';

import { replaceInheritedValueInFenixFilters } from './utils';

import {
    fetchFenixChartData,
    fetchFenixHeatMapChartData,
    fetchFenixTreeMapChartData,
} from '../../clients/fenixClient';
import { buildChartOptions, buildDataRequests } from '../../chartRenderer';

import { t } from '../../i18n';

import s from './HighCharts.module.scss';

// Highcharts plugin for XLSX export using SheetJS
const initHighchartsXLSXExport = (H) => {
    if (!H.getOptions().exporting || typeof XLSX === 'undefined') {
        return;
    }

    H.Chart.prototype.downloadXLSX = function () {
        let rows = this.getDataRows();

        if (!rows || rows.length === 0) return;

        rows = rows.slice(0);

        const ws = XLSX.utils.aoa_to_sheet(rows);
        const wb = XLSX.utils.book_new();

        let filename;
        if (this.options.exporting?.filename) {
            filename = this.options.exporting.filename;
        } else if (this.title?.textStr) {
            filename = this.title.textStr.replace(/ /g, '-').toLowerCase();
        } else {
            filename = 'chart';
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, `${filename}.xlsx`);
    };

    H.getOptions().lang.downloadXLSX = 'Download XLSX';

    H.getOptions().exporting.menuItemDefinitions.downloadXLSX = {
        textKey: 'downloadXLSX',
        onclick: function () {
            this.downloadXLSX();
        },
    };
};

initHighchartsXLSXExport(Highcharts);

// Substring template helper for responsive heatmap labels
Highcharts.Templating.helpers.substr = (s, from, length) =>
    s.substr(from, length);

const buildDynamicFilter = (name, value, isYearPicker = false) => {
    if (!name || value === null || value === undefined) return [];
    if (isYearPicker || name === 'start_date' || name === 'end_date') {
        const year = String(value).substring(0, 4);
        return [
            { key: 'start_date', value: `${year}-01-01` },
            { key: 'end_date', value: `${year}-12-31` },
        ];
    }
    return [{ key: name, value }];
};

const initialDynamicValue = (queries, property) => {
    for (const query of queries) {
        const value = query.filter?.query?.value?.find(
            (item) => item.field === `properties.${property}`
        )?.value;
        if (value !== undefined) return value;
    }
    return null;
};

// Helper to build timeline export rows from chart series data
const getTimelineExportRows = (chart) => {
    const data = chart.series[0]?.options?.data || [];
    const header = ['Date', 'Name', 'Description'];
    return [
        header,
        ...data.map((point) => [
            point.date || '',
            point.name || '',
            point.description || '',
        ]),
    ];
};

// Generate CSV string from rows array
const rowsToCSV = (rows) => {
    return rows
        .map((row) =>
            row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
};

const highchartsExportSettings = {
    enabled: true,
    buttons: {
        contextButton: {
            menuItems: [
                'downloadCSV',
                'downloadXLSX',
                'separator',
                'downloadPNG',
                'downloadJPEG',
                'downloadPDF',
                'downloadSVG',
                'separator',
                'viewFullscreen',
                'printChart',
            ],
        },
    },
};

const HighCharts = (props) => {
    const {
        title = 'Chart title not set',
        displayLargeTitle = false,
        showTitleInToc = false,
        chartType = 'line',
        treeMapLayoutAlgorithm = 'sliceAndDice',

        treeMapBorderWidth = 3,
        treeMapBorderColor = '#fff',

        timelineColor,
        timelineSpacePointsEvenly = false,
        timelineDateOrdering,
        timelineDateFormatting,

        yAxisLabel = '',
        xAxisLabel = '',

        propertySeriesQueries = [],
        // aggregateSeriesQueries,

        yAxisMin = null,
        yAxisMax = null,
        yAxisTickInterval = null,

        decimalPlaces = null,

        xAxisLabelFormattting = 'none',
        xAxisCategorySortingType = 'number',
        xAxisCategoryOrdering = 'asc',

        yAxisLabelFormattting = 'none',
        yAxisCategorySortingType = 'number',
        yAxisCategoryOrdering = 'asc',

        colorScheme = 'blue',

        aggregateDataByCategory = false,

        dynamicProperty = null,

        plotOptions = {},
    } = props;

    const chartId = useId();

    const chartRef = useRef(null);
    const loaderContainerRef = useRef(null);
    const containerRef = useRef(null);
    const [loaderReady, setLoaderReady] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    const countryMeta = useContext(CountryContext);

    const [options, setOptions] = useState(false);
    const [dataFetchError, setDataFetchError] = useState(null);
    const [treeMapTotal, setTreeMapTotal] = useState(null);

    const isDesktop = useMediaQuery('(min-width: 768px)');

    useEffect(() => {
        if (
            loaderContainerRef.current &&
            loaderContainerRef.current.offsetWidth
        ) {
            setLoaderReady(true);
            setContainerWidth(loaderContainerRef.current.offsetWidth);
        }
    }, [loaderContainerRef.current]);

    // for SSR/hydration: fallback to ready after mount if ref is set
    useEffect(() => {
        if (
            loaderContainerRef.current &&
            loaderContainerRef.current.offsetWidth
        ) {
            setLoaderReady(true);
            setContainerWidth(loaderContainerRef.current.offsetWidth);
        }
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined' && typeof Highcharts === 'object') {
            const rootStyles = getComputedStyle(document.body);
            const baseFont =
                rootStyles.getPropertyValue('--font-source-sans-3').trim() ||
                'Arial';

            Highcharts.setOptions({
                credits: {
                    enabled: false,
                },
                chart: {
                    style: {
                        fontFamily: baseFont,
                        fontSize: '12px',
                    },
                },
                title: {
                    style: {
                        fontFamily: baseFont,
                        fontSize: '18px',
                    },
                },
                navigation: {
                    buttonOptions: {
                        align: 'right',
                        verticalAlign: 'top',
                        x: 5,
                        y: -9,
                    },
                },
                xAxis: {
                    // reversed: true,
                    labels: {
                        style: {
                            fontFamily: baseFont,
                            fontSize: '12px',
                        },
                    },
                    title: {
                        style: {
                            fontFamily: baseFont,
                            fontSize: '14px',
                        },
                    },
                },
                yAxis: {
                    labels: {
                        style: {
                            fontFamily: baseFont,
                            fontSize: '12px',
                        },
                    },
                    title: {
                        style: {
                            fontFamily: baseFont,
                            fontSize: '14px',
                        },
                    },
                },
                legend: {
                    itemStyle: {
                        fontFamily: baseFont,
                        fontSize: '12px',
                    },
                },
                tooltip: {
                    style: {
                        fontFamily: baseFont,
                        fontSize: '12px',
                    },
                },
            });
        }
    }, []);

    const dynamicPropertyValue =
        dynamicProperty?.type === 'dynamic_property'
            ? dynamicProperty.value
            : dynamicProperty;
    const dynamicQueryPropertyName =
        dynamicPropertyValue?.property?.field ?? dynamicPropertyValue?.property;
    const hasDynamicProperty = !!dynamicQueryPropertyName;
    const showYearPickerForDynamicProperty =
        dynamicPropertyValue?.optionsSelectStyle === 'year';

    const [dynamicQueryPropertyOptions, setDynamicQueryPropertyOptions] =
        useState([]);
    const [dynamicQueryPropertyValue, setDynamicQueryPropertyValue] = useState(
        hasDynamicProperty
            ? initialDynamicValue(
                  propertySeriesQueries,
                  dynamicQueryPropertyName
              )
            : null
    );

    const handleDynamicPropertyChange = ({ value }) => {
        setDynamicQueryPropertyValue(value);
    };

    useEffect(() => {
        if (
            hasDynamicProperty &&
            !dynamicPropertyValue?.populateAutomatically
        ) {
            setDynamicQueryPropertyOptions(
                dynamicPropertyValue?.propertyFilterOptions ?? []
            );
        }

        (async () => {
            if (
                !hasDynamicProperty ||
                !dynamicPropertyValue?.populateAutomatically
            ) {
                return;
            }

            const seen = new Set();
            let reqPromises = [];
            let reqTypes = [];
            for (const seriesQuery of propertySeriesQueries) {
                const dsType = seriesQuery.dataSource?.type;
                const dsValue = seriesQuery.dataSource?.value;
                const key = `${dsType}:${dsValue}`;
                if (!dsValue || seen.has(key)) continue;
                seen.add(key);
                if (dsType === 'dataset') {
                    reqPromises.push(
                        fetch(
                            `/wt/fenix/graphs/metadata/${encodeURIComponent(dsValue)}/${encodeURIComponent(dynamicQueryPropertyName)}/`
                        )
                    );
                    reqTypes.push('fenix');
                }
            }

            const responses = await Promise.all(reqPromises);
            let options = [];

            for (let i = 0; i < responses.length; i++) {
                const res = responses[i];
                const data = await res.json();

                if (!res.ok) {
                    setDataFetchError({
                        message: t('general.errorDataReqFailed'),
                        detail: `Status ${res.status}: ${data?.error}`,
                    });

                    return;
                }

                const rawValues =
                    reqTypes[i] === 'fenix'
                        ? data.items || data.values || data.unique_values || []
                        : data;

                const values =
                    showYearPickerForDynamicProperty && reqTypes[i] === 'fenix'
                        ? [
                              ...new Set(
                                  rawValues
                                      .map((v) =>
                                          parseInt(
                                              String(v).substring(0, 4),
                                              10
                                          )
                                      )
                                      .filter((n) => !isNaN(n))
                              ),
                          ].sort((a, b) => a - b)
                        : rawValues;

                options = [
                    ...options,
                    ...values.map((v) => ({ value: v, label: v })),
                ];
            }

            setDynamicQueryPropertyOptions(options);
        })();
    }, [dynamicPropertyValue]);

    useEffect(() => {
        const handleResize = () => {
            if (!chartRef.current || chartType !== 'timeline') {
                return;
            }

            const chart = chartRef.current?.chart;
            if (!chart || !containerRef.current) {
                return;
            }

            const newMarginLeft =
                (containerRef.current?.offsetWidth || 0) * -1 + 90;
            const cardWidth = containerRef.current?.offsetWidth - 104;
            document.documentElement.style.setProperty(
                `--timeline-${chartId}-card-width`,
                `${cardWidth}px`
            );

            chart.update(
                {
                    chart: {
                        marginLeft: newMarginLeft,
                    },
                },
                false // don't redraw yet
            );

            chart.reflow(); // important on resize
            chart.redraw();
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // initial call

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchAndFormatBasicChartData = async () => {
        const chartBlock = {
            ...props,
            chartType,
            countryCode: countryMeta?.code,
            labelVariables: {
                countryName: countryMeta?.name,
                countryCode: countryMeta?.code,
            },
            exporting: highchartsExportSettings,
        };
        const requests = buildDataRequests(chartBlock).map((request) => ({
            ...request,
            filterParams: [
                ...replaceInheritedValueInFenixFilters(
                    request.filterParams,
                    countryMeta?.code
                ),
                ...(hasDynamicProperty && dynamicQueryPropertyValue
                    ? buildDynamicFilter(
                          dynamicQueryPropertyName,
                          dynamicQueryPropertyValue,
                          showYearPickerForDynamicProperty
                      )
                    : []),
            ],
        }));
        const responses = await Promise.all(
            requests.map(({ dataset, filterParams }) =>
                fetchFenixChartData(dataset, filterParams)
            )
        );
        const rows = [];
        for (const response of responses) {
            const data = await response.json();
            if (!response.ok) {
                setDataFetchError({
                    message: t('general.errorDataReqFailed'),
                    detail: `Status ${response.status}: ${data?.error}`,
                });
                return;
            }
            rows.push(data.items || []);
        }
        const finalOptions = buildChartOptions(chartBlock, rows);
        if (!finalOptions.series.some((serie) => serie.data.length > 0)) {
            setDataFetchError({
                message: t('general.errorNoSeriesData'),
                detail: null,
            });
            return;
        }
        setOptions(finalOptions);
    };

    const fetchAndFormatTreeMapChartData = async () => {
        const seriePromises = propertySeriesQueries.map((query) => {
            if (query?.dataSource?.type === 'dataset') {
                const dataset = query.dataSource?.value;
                const baseFilterParams = replaceInheritedValueInFenixFilters(
                    query.filterParams || query.filter_params || [],
                    countryMeta?.code
                );
                const filterParams =
                    hasDynamicProperty && dynamicQueryPropertyValue
                        ? [
                              ...baseFilterParams,
                              ...buildDynamicFilter(
                                  dynamicQueryPropertyName,
                                  dynamicQueryPropertyValue,
                                  showYearPickerForDynamicProperty
                              ),
                          ]
                        : baseFilterParams;

                return fetchFenixTreeMapChartData(
                    dataset,
                    filterParams,
                    query.serie,
                    query.hierarchyLevels || query.hierarchy_levels
                );
            }
            return null;
        });

        // The renderer pairs each row group with its own series' hierarchy
        // levels (propertySeriesQueries[i]), so keep one group per series
        // instead of flattening — flattened, every series was coloured by
        // series 0's levels.
        const responses = await Promise.all(seriePromises);
        const rowGroups = [];

        for (const res of responses) {
            if (!res) {
                rowGroups.push([]);
                continue;
            }
            const data = await res.json();

            if (!res.ok) {
                setDataFetchError({
                    message: t('general.errorDataReqFailed'),
                    detail: `Status ${res.status}: ${data?.error}`,
                });
                return;
            }

            rowGroups.push(data.treemap);
        }

        const allPoints = rowGroups.flat();

        // Calculate initial total (sum of all values at the deepest level)
        const totalValue = allPoints.reduce(
            (sum, point) => sum + (point.value || 0),
            0
        );
        const rootLevelTotal = totalValue;
        setTreeMapTotal(rootLevelTotal);

        const rendererOptions = buildChartOptions(
            {
                ...props,
                chartType: 'treemap',
                layoutAlgorithm: treeMapLayoutAlgorithm,
                borderWidth: treeMapBorderWidth,
                borderColor: treeMapBorderColor,
                exporting: highchartsExportSettings,
                plotOptions,
            },
            rowGroups
        );
        const finalOptions = {
            ...rendererOptions,
            chart: {
                events: {
                    drilldown: function (e) {
                        // When drilling down, the total should be the value of the item we're drilling into
                        setTreeMapTotal(e.point.value || 0);
                    },
                    drillup: function (e) {
                        // When drilling up, recalculate the total for the current level
                        // If we're at root level, use rootLevelTotal
                        if (!e.seriesOptions || !e.seriesOptions.rootId) {
                            setTreeMapTotal(rootLevelTotal);
                        } else {
                            // Find the parent we're drilling up to and use its value
                            const parentId = e.seriesOptions.rootId;
                            const parentPoint = allPoints.find(
                                (point) => point.id === parentId
                            );
                            setTreeMapTotal(
                                parentPoint ? parentPoint.value : rootLevelTotal
                            );
                        }
                    },
                },
            },
            series: rendererOptions.series.map((serie) => ({
                ...serie,
                point: {
                    events: {
                        click: function () {
                            if (this.drilldown !== false) {
                                setTreeMapTotal(this.value || 0);
                            }
                        },
                    },
                },
                events: {
                    afterSetRootNode: function (e) {
                        if (e.newRootId) {
                            const rootPoint = allPoints.find(
                                (point) => point.id === e.newRootId
                            );
                            setTreeMapTotal(rootPoint ? rootPoint.value : 0);
                        } else {
                            setTreeMapTotal(rootLevelTotal);
                        }
                    },
                },
                breadcrumbs: {
                    ...serie.breadcrumbs,
                    events: {
                        click: function (e) {
                            if (e.newRootId) {
                                const rootPoint = allPoints.find(
                                    (point) => point.id === e.newRootId
                                );
                                setTreeMapTotal(
                                    rootPoint ? rootPoint.value : 0
                                );
                            } else {
                                setTreeMapTotal(rootLevelTotal);
                            }
                        },
                    },
                },
            })),
        };

        setOptions(finalOptions);
    };

    const fetchAndFormatHeatMapChartData = async () => {
        const chartBlock = {
            ...props,
            chartType: 'heatmap',
            title,
            xAxisLabel,
            yAxisLabel,
            xAxisCategoryOrdering,
            xAxisCategorySortingType,
            xAxisLabelFormattting,
            yAxisCategoryOrdering,
            yAxisCategorySortingType,
            yAxisLabelFormattting,
            colorScheme,
            decimalPlaces,
            labelVariables: {
                countryName: countryMeta?.name,
                countryCode: countryMeta?.code,
            },
            exporting: {
                ...highchartsExportSettings,
                buttons: {
                    ...highchartsExportSettings.buttons,
                    contextButton: {
                        ...highchartsExportSettings.buttons.contextButton,
                        y: -13,
                    },
                },
            },
            isDesktop,
        };

        const seriePromises = propertySeriesQueries.map((query) => {
            if (query?.dataSource?.type === 'dataset') {
                const baseFilterParams = replaceInheritedValueInFenixFilters(
                    query.filterParams || query.filter_params || [],
                    countryMeta?.code
                );
                const filterParams =
                    hasDynamicProperty && dynamicQueryPropertyValue
                        ? [
                              ...baseFilterParams,
                              ...buildDynamicFilter(
                                  dynamicQueryPropertyName,
                                  dynamicQueryPropertyValue,
                                  showYearPickerForDynamicProperty
                              ),
                          ]
                        : baseFilterParams;
                return fetchFenixHeatMapChartData(
                    query.dataSource.value,
                    filterParams,
                    query.serie
                );
            }

            return null;
        });

        const responses = await Promise.all(seriePromises);

        let aggregate = null;
        for (const res of responses) {
            const data = await res.json();

            if (!res.ok) {
                setDataFetchError({
                    message: t('general.errorDataReqFailed'),
                    detail: `Status ${res.status}: ${data?.error}`,
                });
                return;
            }

            aggregate = {
                xAxisCategories: data.xAxisCategories || [],
                yAxisCategories: data.yAxisCategories || [],
                data: data.data || [],
            };
        }

        if (!aggregate || !aggregate.data.length) {
            setDataFetchError({
                message: t('general.errorNoSeriesData'),
                detail: null,
            });
            return;
        }

        setOptions(buildChartOptions(chartBlock, [aggregate]));
    };

    const fetchAndFormatTimelineChartData = async () => {
        const chartBlock = {
            ...props,
            chartType: 'timeline',
            dateOrdering: timelineDateOrdering,
            dateFormattting: timelineDateFormatting,
            spacePointsEvenly: timelineSpacePointsEvenly,
            chartId,
            isDesktop,
        };
        const requests = buildDataRequests(chartBlock).map((request) => ({
            ...request,
            filterParams: [
                ...replaceInheritedValueInFenixFilters(
                    request.filterParams,
                    countryMeta?.code
                ),
                ...(hasDynamicProperty && dynamicQueryPropertyValue
                    ? buildDynamicFilter(
                          dynamicQueryPropertyName,
                          dynamicQueryPropertyValue,
                          showYearPickerForDynamicProperty
                      )
                    : []),
            ],
        }));
        const responses = await Promise.all(
            requests.map(({ dataset, filterParams }) =>
                fetchFenixChartData(dataset, filterParams)
            )
        );
        const rows = [];
        for (const response of responses) {
            const data = await response.json();
            if (!response.ok) {
                setDataFetchError({
                    message: t('general.errorDataReqFailed'),
                    detail: `Status ${response.status}: ${data?.error}`,
                });
                return;
            }
            rows.push(data.items || []);
        }

        const rendererOptions = buildChartOptions(chartBlock, rows);

        if (!rendererOptions.series.some((serie) => serie.data.length > 0)) {
            setDataFetchError({
                message: t('general.errorNoSeriesData'),
                detail: null,
            });

            return;
        }

        // The card width is a DOM measurement written to a document-level CSS
        // custom property — a side effect that can't live in the pure renderer.
        const cardWidth = loaderContainerRef.current?.offsetWidth - 104;
        document.documentElement.style.setProperty(
            `--timeline-${chartId}-card-width`,
            `${cardWidth}px`
        );

        // The renderer emits the pure timeline options; only the function-
        // valued slots (export handlers, the load handler, the tooltip
        // formatter/positioner) and the DOM-measured margin merge caller-side.
        const finalOptions = {
            ...rendererOptions,
            exporting: {
                ...rendererOptions.exporting,
                menuItemDefinitions: {
                    downloadCSV: {
                        ...rendererOptions.exporting.menuItemDefinitions
                            .downloadCSV,
                        onclick: function () {
                            const rows = getTimelineExportRows(this);
                            const csv = '\uFEFF' + rowsToCSV(rows);
                            const blob = new Blob([csv], {
                                type: 'text/csv;charset=utf-8',
                            });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download =
                                (this.title?.textStr || 'chart')
                                    .replace(/ /g, '-')
                                    .toLowerCase() + '.csv';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        },
                    },
                    downloadXLSX: {
                        ...rendererOptions.exporting.menuItemDefinitions
                            .downloadXLSX,
                        onclick: function () {
                            const rows = getTimelineExportRows(this);
                            const ws = XLSX.utils.aoa_to_sheet(rows);
                            const wb = XLSX.utils.book_new();
                            const filename = (this.title?.textStr || 'chart')
                                .replace(/ /g, '-')
                                .toLowerCase();
                            XLSX.utils.book_append_sheet(wb, ws, 'Data');
                            XLSX.writeFile(wb, `${filename}.xlsx`);
                        },
                    },
                },
            },
            chart: {
                ...rendererOptions.chart,
                marginLeft: containerWidth * -1 + 90,
                events: {
                    load: function () {
                        const getTimelinePointIndex = (el) => {
                            if (!el || !el.classList) return null;
                            for (const cls of el.classList) {
                                const match = cls.match(
                                    /^timeline-point-index--(\d+)$/
                                );
                                if (match) return Number(match[1]);
                            }
                            return null;
                        };

                        const chart = this;

                        // detect if device actually supports hover
                        const hoverMedia =
                            window.matchMedia &&
                            window.matchMedia('(hover: hover)');
                        const hasHover = hoverMedia
                            ? hoverMedia.matches
                            : false;
                        const isTouchMode = !hasHover; // treat "no hover" as mobile

                        function bindInfoButtons() {
                            const buttons = chart.container.querySelectorAll(
                                '.timeline-info-button'
                            );

                            buttons.forEach((btn) => {
                                const index = getTimelinePointIndex(btn);
                                if (index == null) return;

                                const point = chart.series[0].points[index];
                                if (!point) return;

                                // clear old handlers to avoid duplicates on redraw
                                btn.onmouseenter = null;
                                btn.onmouseleave = null;
                                btn.onclick = null;

                                if (!isTouchMode) {
                                    btn.onmouseenter = function () {
                                        chart._activeTooltipButton = btn;
                                        chart.tooltip.refresh(point);
                                    };

                                    btn.onmouseleave = function () {
                                        chart.tooltip.hide();
                                        chart._activeTooltipButton = null;
                                    };
                                } else {
                                    btn.onclick = function (e) {
                                        e.stopPropagation(); // don't trigger document click

                                        // toggle for same point
                                        if (
                                            chart._activeMobilePoint === point
                                        ) {
                                            chart.tooltip.hide();
                                            chart._activeMobilePoint = null;
                                            chart._activeTooltipButton = null;
                                        } else {
                                            chart._activeTooltipButton = btn;
                                            chart.tooltip.refresh(point);
                                            chart._activeMobilePoint = point;
                                        }
                                    };
                                }
                            });
                        }

                        bindInfoButtons();
                        Highcharts.addEvent(chart, 'redraw', bindInfoButtons);

                        if (isTouchMode) {
                            document.addEventListener('click', function () {
                                chart.tooltip.hide();
                                chart._activeMobilePoint = null;
                            });
                        }
                    },
                },
            },
            tooltip: {
                ...rendererOptions.tooltip,
                positioner: function (labelWidth, labelHeight, point) {
                    const chart = this.chart;
                    const button = chart._activeTooltipButton;

                    if (button) {
                        const buttonRect = button.getBoundingClientRect();
                        const chartRect =
                            chart.container.getBoundingClientRect();
                        const x = buttonRect.right - chartRect.left + 8;
                        const y =
                            buttonRect.top - chartRect.top + buttonRect.height; // / 2 - 10;

                        // Keep tooltip within chart bounds
                        const maxX = chart.chartWidth - labelWidth - 10;
                        const maxY = chart.chartHeight - labelHeight - 10;

                        return {
                            x: Math.min(Math.max(0, x), maxX),
                            y: Math.min(Math.max(10, y), maxY),
                        };
                    }
                    // Fallback to default position near the point
                    return { x: point.plotX + 20, y: point.plotY };
                },
                formatter: function () {
                    const p = this.point;
                    return `
                        <div class="timeline-tooltip">
                            ${p.description || ''}
                        </div>
                    `;
                },
            },
        };

        setOptions(finalOptions);
    };

    useEffect(() => {
        if (!loaderReady) {
            return;
        }

        // avoid race condition when loaded modules have not yet initialized
        setTimeout(() => {
            if (chartType === 'treemap') {
                fetchAndFormatTreeMapChartData();
            } else if (chartType === 'heatmap') {
                fetchAndFormatHeatMapChartData();
            } else if (chartType === 'timeline') {
                fetchAndFormatTimelineChartData();
            } else {
                // Standard, polar, and mixed charts all render through the
                // shared chart renderer. Mixed blocks reach this branch as
                // chartType="line" with isMixedChart set.
                fetchAndFormatBasicChartData();
            }
        }, 20);
    }, [dynamicQueryPropertyValue, loaderReady, isDesktop]);

    const chartTitleCls = classNames(s.ChartTitle, {
        [s['ChartTitle--Large']]: displayLargeTitle,
    });

    if (!loaderReady || !options || dataFetchError) {
        return (
            <div className={s.Root} ref={loaderContainerRef}>
                <div className={s.ChartHeader}>
                    {title && (
                        <h3
                            className={chartTitleCls}
                            data-hide-title-in-toc={!showTitleInToc}>
                            {title}
                        </h3>
                    )}
                </div>
                <div className={s.LoaderContainer}>
                    {!dataFetchError && <SkeletonLoader size="cover" />}
                    {dataFetchError && (
                        <div className={s.ErrorWrapper}>
                            <div className={s.Error}>
                                <DataErrorItem
                                    title={t('general.errorFetchingData')}
                                    description={dataFetchError?.message}
                                    detail={dataFetchError?.detail}
                                    icon="error"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const formatTotalValue = (value) => {
        if (value >= 1000000000) {
            return '$' + (value / 1000000000).toFixed(1) + 'B';
        } else if (value >= 1000000) {
            return '$' + (value / 1000000).toFixed(1) + 'M';
        } else if (value >= 1000) {
            return '$' + (value / 1000).toFixed(1) + 'K';
        } else {
            return '$' + value.toString();
        }
    };

    const chartContainerCls = classNames('highcharts-light', s.ChartContainer, {
        [s['ChartContainerTimeline']]: chartType === 'timeline',
        [s['ChartContainerHeatmap']]: chartType === 'heatmap',
    });

    return (
        <div className={s.Root} id={`hc-${chartId}`} ref={containerRef}>
            <>
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                        #hc-${chartId} .highcharts-point {
                            stroke: ${timelineColor} !important;
                        }

                    `,
                    }}
                />
            </>
            <div className={s.ChartHeader}>
                <h3 className={chartTitleCls}>{title}</h3>
                {hasDynamicProperty && dynamicQueryPropertyOptions?.length ? (
                    <div className={s.DynamicPropertyContainer}>
                        {showYearPickerForDynamicProperty ? (
                            <YearPicker
                                selectedYear={dynamicQueryPropertyValue}
                                setSelectedYear={handleDynamicPropertyChange}
                                years={dynamicQueryPropertyOptions.map((o) => {
                                    const n = parseInt(
                                        String(o.value).substring(0, 4),
                                        10
                                    );
                                    return isNaN(n) ? o.value : n;
                                })}
                                modifiers={['WithGrayBg']}
                            />
                        ) : (
                            <OverviewSelect
                                value={dynamicQueryPropertyValue}
                                options={dynamicQueryPropertyOptions}
                                handleChange={handleDynamicPropertyChange}
                            />
                        )}
                    </div>
                ) : null}
            </div>
            <div className={chartContainerCls}>
                {chartType === 'treemap' && treeMapTotal !== null && (
                    <div className={s.TreeMapTotal}>
                        <strong>Total: {formatTotalValue(treeMapTotal)}</strong>
                    </div>
                )}
                <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={options}
                />
            </div>
        </div>
    );
};

HighCharts.propTypes = {
    title: PropTypes.string,
    displayLargeTitle: PropTypes.bool,
    showTitleInToc: PropTypes.bool,

    chartType: PropTypes.string,
    isMixedChart: PropTypes.bool,
    polarChartValueType: PropTypes.string,
    treeMapLayoutAlgorithm: PropTypes.string,

    treeMapBorderWidth: PropTypes.number,
    treeMapBorderColor: PropTypes.string,

    timelineDateOrdering: PropTypes.string,
    timelineDateFormatting: PropTypes.string,

    yAxisLabel: PropTypes.string,
    yAxisMin: PropTypes.number,
    yAxisMax: PropTypes.number,
    yAxisTickInterval: PropTypes.number,
    yAxisStackValues: PropTypes.bool,

    decimalPlaces: PropTypes.number,

    xAxisLabel: PropTypes.string,
    xAxisLabelFormattting: PropTypes.string,
    xAxisCategorySortingType: PropTypes.string,
    xAxisCategoryOrdering: PropTypes.string,

    yAxisLabelFormattting: PropTypes.string,
    yAxisCategorySortingType: PropTypes.string,
    yAxisCategoryOrdering: PropTypes.string,

    colorScheme: PropTypes.string,

    propertySeriesQueries: PropTypes.array,

    dynamicProperty: PropTypes.object,
    plotOptions: PropTypes.object,
    timelineSpacePointsEvenly: PropTypes.bool,
};

export default HighCharts;
